import { afterNextRender, computed, inject, Injectable, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { TIME_TO_OPENING } from '@components/promotion/config';
import { CacheService } from '@services/cache';
import { ContextService } from '@services/context';
import { ElasticSearchService } from '@services/elastic-search';
import { LogsService } from '@services/logs';
import { PageTypeService } from '@services/page-type';
import { catchError, of } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class PromotionService {
  readonly isPopinOpen = signal(false);
  readonly #logsService = inject(LogsService);
  readonly #promotion = toSignal(
    inject(ElasticSearchService)
      .getGeneralPromotion()
      .pipe(
        catchError((err: unknown) => {
          this.#logsService.error('PromotionService', 'General promotion fetch failed', err);
          return of(undefined);
        })
      )
  );
  readonly banner = computed(() => this.#promotion()?.banner);
  readonly content = computed(() => {
    const paragraphsArray = this.banner()?.content.split('<p>');
    paragraphsArray?.shift();
    if (paragraphsArray) {
      return paragraphsArray.map((paragraph) => `<p>${paragraph}`);
    }
    return [];
  });
  readonly popin = computed(() => this.#promotion()?.popin);
  readonly #pageType = toSignal(inject(PageTypeService).getPageType$());
  readonly #excludePageType = computed(() => this.#promotion()?.excludePageType);
  readonly isAllowed = computed(() => !this.#excludePageType()?.includes(this.#pageType()!));
  readonly #contextService = inject(ContextService);
  readonly #cacheService = inject(CacheService);

  constructor() {
    afterNextRender(() => {
      this.#startPopinCountdown();
    });
  }

  closePopin() {
    this.isPopinOpen.set(false);
  }

  #startPopinCountdown(): void {
    if (this.#contextService.isServer()) return;
    void this.#cacheService
      .get('isPromotionViewed')
      .then((isPromotionViewed) => {
        if (!isPromotionViewed) {
          setTimeout(() => {
            if (this.isAllowed()) {
              this.isPopinOpen.set(true);
              this.#cacheService
                .set('isPromotionViewed', true, 2592000)
                .catch((error: unknown) =>
                  this.#logsService.warn('PromotionService', 'Failed to persist promotion popin state', error)
                );
            }
          }, TIME_TO_OPENING * 1000);
        }
      })
      .catch((error: unknown) =>
        this.#logsService.warn('PromotionService', 'Failed to read promotion popin state', error)
      );
  }
}
